import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavBarLateral } from "../NavBarLateral";
import { history } from "../../../_helpers";
import alertService from "../../../_services/cart/alert.service";
import { ConfirmarAccion } from "./ConfirmarAccion";
import suscripcionService, { formatoFecha, formatoPrecio } from "../../../_services/cart/suscripcion.service";

/**
 * Mi cuenta > Plan: la suscripcion de la cuenta, cuanto del plan se esta usando y los cobros.
 *
 * Portada de la solapa "Pagos y facturación" de tienda-admin (`pages/mi-estudio-usuarios`, 13b5a5b): las
 * cuatro tarjetas de resumen (plan, monto, estado, proximo cobro) y el listado de pagos con los mismos
 * textos. Se suma el uso del plan (usuarios y publicaciones activas contra el tope), que tienda-admin
 * tenia armado pero comentado. La columna de factura llega con la facturacion por ARCA.
 */

const estadoSuscripcionLabel = (estado) => {
    switch (estado) {
        case "ACTIVE": return "Activa";
        case "PAUSED": return "Pausada";
        case "PENDING": return "Pendiente";
        case "CANCELLED": return "Cancelada";
        case "EXPIRED": return "Vencida";
        default: return estado || "—";
    }
};

const estadoPagoLabel = (status) => {
    switch (status) {
        case "APPROVED": return "Acreditado";
        case "PENDING": return "Pendiente";
        case "REJECTED": return "Rechazado";
        case "REFUNDED": return "Devuelto";
        default: return status;
    }
};

/** Barra de uso de un tope del plan. Sin tope se muestra "ilimitado". */
const Uso = ({ etiqueta, usado, max }) => {
    const porcentaje = max ? Math.min(100, Math.round((usado / max) * 100)) : 0;
    const lleno = max && usado >= max;
    return (
        <div className="mi-plan__uso">
            <div className="mi-plan__uso-head">
                <p>{etiqueta}</p>
                <p>
                    <strong>{usado}</strong> <span>/ {max ?? "ilimitado"}</span>
                </p>
            </div>
            {max ? (
                <div className="mi-plan__barra">
                    <div className={lleno ? "mi-plan__barra-relleno mi-plan__barra-relleno--lleno" : "mi-plan__barra-relleno"} style={{ width: `${porcentaje}%` }} />
                </div>
            ) : null}
        </div>
    );
};

export const MiPlan = () => {
    const [estado, setEstado] = useState(null);
    const [pagos, setPagos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [confirmarCancelar, setConfirmarCancelar] = useState(false);

    useEffect(() => {
        Promise.all([suscripcionService.estado(true), suscripcionService.pagos()])
            .then(([e, p]) => {
                setEstado(e);
                setPagos(p);
            })
            .catch((err) => alertService.error(err))
            .finally(() => setCargando(false));
    }, []);

    const cancelar = () => {
        setConfirmarCancelar(false);
        suscripcionService.cancelar()
            .then((e) => {
                setEstado(e);
                alertService.msg("Cancelaste tu suscripción", "success");
            })
            .catch((err) => alertService.error(err));
    };

    // Paga por MP, vigente y sin cancelar: es lo unico que se puede cancelar desde aca.
    const cancelable = estado?.vigente && !estado.trial && !estado.fundador && !estado.cancelada && estado.nextPaymentDate;

    const proximoCobro = () => {
        if (!estado) return "—";
        if (estado.fundador) return "Sin cargo";
        if (estado.cancelada) return "Sin cobros (cancelada)";
        if (estado.trial) return estado.endDate ? `Prueba hasta ${formatoFecha(estado.endDate)}` : "—";
        return formatoFecha(estado.nextPaymentDate);
    };

    return (
        <div className="dashboard">
            <NavBarLateral handleClose={() => {}} />

            <div className="dashboard__content cuenta-usuarios">
                <div className="dashboard__content-header cuenta-usuarios__header">
                    <div className="dashboard__content-title">
                        <p className="cuenta-usuarios__eyebrow">MI CUENTA</p>
                        <h2>Plan y pagos</h2>
                        <p className="panel__bajada">Los cobros de tu suscripción y lo que estás usando de tu plan.</p>
                    </div>
                    {!estado?.fundador && (
                        <button type="button" className="button" data-type="accent" onClick={() => history.push("/planes")}>
                            <FontAwesomeIcon icon="fa-solid fa-arrow-up-right-dots" />
                            {estado?.vigente && !estado?.trial ? "Cambiar de plan" : "Elegir plan"}
                        </button>
                    )}
                </div>

                {cargando && <p className="panel__estado-carga">Cargando…</p>}

                {!cargando && estado && (
                    <>
                        {!estado.vigente && (
                            <div className="planes__banner planes__banner--vencido mi-plan__aviso">
                                <div>
                                    <h3>{estado.trial ? "Terminó tu prueba gratis" : "Tu suscripción no está activa"}</h3>
                                    <p>Tus publicaciones no se muestran ni se pueden comprar hasta que elijas un plan. Apenas se acredite el pago vuelven solas.</p>
                                </div>
                            </div>
                        )}
                        {estado.cancelada && (
                            <div className="planes__banner mi-plan__aviso">
                                <div>
                                    <h3>Cancelaste tu suscripción</h3>
                                    <p>No se te va a cobrar más. Seguís vendiendo hasta el {formatoFecha(estado.endDate)}; después tus publicaciones dejan de mostrarse hasta que elijas un plan.</p>
                                </div>
                            </div>
                        )}
                        {estado.pendientePago && (
                            <div className="planes__banner mi-plan__aviso">
                                <div>
                                    <h3>Estamos esperando la confirmación de Mercado Pago</h3>
                                    <p>Apenas Mercado Pago autorice el débito, tu plan nuevo queda activo.</p>
                                </div>
                            </div>
                        )}

                        {/* Resumen de la suscripción */}
                        {estado.tieneSuscripcion ? (
                            <section className="mi-plan__resumen">
                                <div className="cuenta-usuarios__card">
                                    <p className="cuenta-usuarios__eyebrow">Plan</p>
                                    <p className="mi-plan__valor">
                                        {estado.planNombre || "—"}
                                        {estado.trial && <span className="cuenta-usuarios__sistema">Prueba</span>}
                                    </p>
                                </div>
                                <div className="cuenta-usuarios__card">
                                    <p className="cuenta-usuarios__eyebrow">Monto mensual</p>
                                    <p className="mi-plan__valor">
                                        {estado.fundador ? "Sin cargo" : formatoPrecio(estado.montoMensual)}
                                    </p>
                                </div>
                                <div className="cuenta-usuarios__card">
                                    <p className="cuenta-usuarios__eyebrow">Estado</p>
                                    <p className={estado.vigente ? "mi-plan__valor mi-plan__valor--ok" : "mi-plan__valor"}>
                                        {estado.vigente ? "Activa" : estadoSuscripcionLabel(estado.status)}
                                    </p>
                                </div>
                                <div className="cuenta-usuarios__card">
                                    <p className="cuenta-usuarios__eyebrow">Próximo cobro</p>
                                    <p className="mi-plan__valor">{proximoCobro()}</p>
                                </div>
                            </section>
                        ) : (
                            <div className="cuenta-usuarios__card cuenta-usuarios__texto">
                                Todavía no hay una suscripción asociada a esta cuenta.
                            </div>
                        )}

                        {estado.incluyeSubdominio && (
                            <div className="planes__banner mi-plan__aviso">
                                <div>
                                    <h3>Tu plan incluye tienda propia</h3>
                                    <p>Tu dirección, tu logo y solo tus productos. Configurala desde Mi tienda.</p>
                                </div>
                                <button type="button" className="button" data-type="accent" onClick={() => history.push("/vendedor/tienda")}>
                                    Configurar mi tienda
                                </button>
                            </div>
                        )}

                        {/* Uso del plan. Sin plan vigente los topes no rigen (no puede publicar nada mas): no se muestran. */}
                        {estado.vigente && (
                        <section className="cuenta-usuarios__card">
                            <p className="cuenta-usuarios__eyebrow">Uso del plan</p>
                            <Uso etiqueta="Publicaciones activas" usado={estado.publicacionesActivas} max={estado.maxPublicacionesActivas} />
                            <Uso etiqueta="Usuarios" usado={estado.usuarios} max={estado.maxUsers} />
                        </section>
                        )}

                        {cancelable && (
                            <button type="button" className="suscripcion-checkout__volver" onClick={() => setConfirmarCancelar(true)}>
                                Cancelar suscripción
                            </button>
                        )}

                        {/* Listado de pagos */}
                        <section className="cuenta-usuarios__card cuenta-usuarios__tabla-card">
                            {pagos.length === 0 && (
                                <p className="cuenta-usuarios__vacio">Todavía no registramos pagos de tu suscripción.</p>
                            )}
                            {pagos.length > 0 && (
                                <table className="cuenta-usuarios__tabla">
                                    <thead>
                                        <tr>
                                            <th>Fecha</th>
                                            <th>Concepto</th>
                                            <th>Monto</th>
                                            <th>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagos.map((p) => (
                                            <tr key={p.id}>
                                                <td>{formatoFecha(p.fechaPago)}</td>
                                                <td>{p.concepto || "Suscripción"}</td>
                                                <td>{formatoPrecio(p.amount)}</td>
                                                <td>
                                                    <span className={p.status === "APPROVED" ? "cuenta-usuarios__rol cuenta-usuarios__rol--admin" : "cuenta-usuarios__rol"}>
                                                        {estadoPagoLabel(p.status)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </section>
                    </>
                )}
            </div>

            {confirmarCancelar && (
                <ConfirmarAccion
                    mensaje={`¿Cancelar tu suscripción? No se te cobra más y seguís vendiendo hasta el ${formatoFecha(estado.nextPaymentDate)}. Después tus publicaciones dejan de mostrarse hasta que elijas un plan.`}
                    confirmLabel="Cancelar suscripción"
                    cancelLabel="Volver"
                    onConfirmar={cancelar}
                    onCancelar={() => setConfirmarCancelar(false)}
                />
            )}
        </div>
    );
};
